import React, { useCallback, useEffect, useState } from "react";

// types
import { useAppDispatch } from "../../../store/hooks";
import type {
  AdminsData,
  ErrorRegisterAdmin,
  RegisterAdmin,
} from "../../../store/types/settings";
import type { ErrorWarning } from "../../../store/types/misc";
import { validateEmail } from "../../../services/formatter/validate";
import {
  getAdmins,
  registerAdmin,
  updateAdmin,
} from "../../../services/api/settings";
import { toast } from "sonner";

export const useRegister = (organizationId: string) => {
  const dispatch = useAppDispatch();
  const [emailExist, emailExistSet] = React.useState<ErrorWarning>("");
  const [loading, setLoading] = useState(false);
  const [data, dataSet] = React.useState<RegisterAdmin>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "ORG_ADMIN",
    organizationId: organizationId || "",
  });

  const [error, errorSet] = React.useState<ErrorRegisterAdmin>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  // update organizationId when it changes
  React.useEffect(() => {
    if (organizationId && organizationId !== data.organizationId) {
      dataSet((prev) => ({ ...prev, organizationId }));
    }
  }, [organizationId, data.organizationId]);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    dataSet((prev) => ({ ...prev, [name]: value }));
    if (name === "email") {
      emailExistSet("");
    }
  };

  const onChangeHandle = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    dataSet((prev) => ({ ...prev, [name]: value }));
  };

  // Validation function - validates trimmed values
  const onValidate = (trimmedUser: RegisterAdmin): boolean => {
    let error: boolean = true;
    if (!trimmedUser.firstName) {
      errorSet((prev) => ({ ...prev, firstName: "warning" }));
      error = false;
    }
    if (!trimmedUser.lastName) {
      errorSet((prev) => ({ ...prev, lastName: "warning" }));
      error = false;
    }
    if (!trimmedUser.email || !validateEmail(trimmedUser.email) || emailExist) {
      errorSet((prev) => ({ ...prev, email: "warning" }));
      error = false;
    }
    if (!trimmedUser.password) {
      errorSet((prev) => ({ ...prev, password: "warning" }));
      error = false;
    }
    if (!trimmedUser.organizationId) {
      error = false;
    }
    return error;
  };

  // submit function with whitespace trimming
  const onSubmit = async () => {
    // user data with trimmed email and password
    const trimmedUser = {
      ...data,
      email: data.email.trim(),
      password: data.password.trim(),
      organizationId: data.organizationId,
    };

    // Update the state with trimmed values
    setLoading(true);
    dataSet(trimmedUser);

    // Validate with trimmed values
    if (onValidate(trimmedUser)) {
      await registerAdmin(dispatch, trimmedUser);
    }
    setLoading(false);
  };

  const onUpdate = async () => {
    try {
      // await updateAdmin(dispatch, adminData?.id!, {
      //   ...data,
      //   name: "",
      //   contactEmail: "",
      //   contactPhone: "",
      //   address: "",
      //   registrationNo: "",
      // });
      toast.success("Admin updated successfully");

      // Call the onSuccess callback if provided
      // if (onSuccess) {
      //   onSuccess();
      // }
    } catch (error) {
      toast.error("Failed to update candidate");
      throw error;
    }
  };

  return {
    data,
    loading,
    emailExist,
    error,
    onChangeHandler,
    onChangeHandle,
    onSubmit,
    onUpdate,
  };
};

export const useAdmins = (id: string) => {
  const [data, setData] = useState<AdminsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAdmins = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getAdmins(id);
      setData(response.data); // response.data is AdminsData
    } catch (err) {
      const errorMessage = "Failed to fetch admins details";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  return { data, loading, error, refetch: fetchAdmins };
};
